/**
 * Semicircular health-score gauge — ported from the design prototype
 * (business-report.js `gauge()`). A gradient arc from red to green, a white
 * needle dot at the score position, and 0/25/50/75/100 ticks.
 *
 * Replaces `react-gauge-component`: the hand-rolled SVG is ~40 lines, matches
 * the design exactly, and theme-adapts through CSS variables (the needle dot
 * is `var(--card)` so it reads as a hole in the arc on dark cards too).
 */

const R = 78;
const CX = 110;
const CY = 118;
const ARC_W = 22;

function pt(a: number, r: number = R): [number, number] {
  return [CX + r * Math.cos(Math.PI * (1 - a)), CY - r * Math.sin(Math.PI * (1 - a))];
}

const TICKS = [0, 25, 50, 75, 100];

export default function Gauge({ score, maxScore = 100 }: { score: number; maxScore?: number }) {
  const clamped = Math.max(0, Math.min(maxScore, score));
  const [x0, y0] = pt(0);
  const [x1, y1] = pt(1);
  const [nx, ny] = pt(clamped / maxScore);

  return (
    <svg viewBox="0 0 220 146" className="block w-full max-w-[200px]" role="img" aria-label={`Score ${score} of ${maxScore}`}>
      <defs>
        <linearGradient id="report-gauge-grad" x1="0" y1="1" x2="1" y2="1">
          <stop offset="0" stopColor="#EF4444" />
          <stop offset=".28" stopColor="#F97316" />
          <stop offset=".5" stopColor="#F5C518" />
          <stop offset=".72" stopColor="#84CC16" />
          <stop offset="1" stopColor="#16A34A" />
        </linearGradient>
      </defs>
      <path
        d={`M${x0.toFixed(2)} ${y0.toFixed(2)} A${R} ${R} 0 0 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`}
        stroke="url(#report-gauge-grad)"
        strokeWidth={ARC_W}
        fill="none"
        strokeLinecap="round"
      />
      <circle cx={nx.toFixed(2)} cy={ny.toFixed(2)} r="11" fill="var(--card)" />
      {TICKS.map((t) => {
        const end = t === 0 || t === 100;
        const [tx, ty] = pt(t / 100, R + (end ? 0 : 21));
        return (
          <text
            key={t}
            x={tx.toFixed(1)}
            y={(ty + (end ? 20 : 4)).toFixed(1)}
            textAnchor="middle"
            className="fill-muted-foreground/70 font-sans text-[11px]"
          >
            {t}
          </text>
        );
      })}
    </svg>
  );
}
